import { Fragment, useEffect, useState } from 'react'
import { getContactChannels, saveContactChannels } from '../../db/api.js'
import whatsappIcon from '../../assets/whatsapp.png'
import { formatContactPhone, formatContactEmail } from './contactInputFormat.js'


// Modo demo: prévia local por contato. Fora dele, telefones e e-mails vêm de
// user_data_contact_channels (1:N) via /crm/contacts/{id}/channels.
const demoChannels = new Map()
const demo = import.meta.env.DEV && import.meta.env.MODE === 'demo'
// Mesmo formato salvo por ChatPhoneSetup (+DDI+número): o chat casa a conversa pelos dígitos.
const toStoredPhone = (value) => {
  const digits = value.replace(/\D/g, '')
  return `+${!value.trim().startsWith('+') && [10, 11].includes(digits.length) ? `55${digits}` : digits}`
}
const clean = (values) => [...new Set(values.map((value) => value.trim()).filter(Boolean))]

export default function DealLeadContacts({ lead, onOpenChat, showDetails = true, onToggleDetails, onChannelsSaved }) {
  const contactId = lead.contact_id || lead.id
  const [channels, setChannels] = useState(() => (demo && demoChannels.get(contactId)) || {
    whatsapp: lead.telefone ? [lead.telefone] : [],
    emails: lead.email ? [lead.email] : [],
  })
  const [draft, setDraft] = useState(channels)
  const [editing, setEditing] = useState(false)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    if (demo || !contactId) return
    let alive = true
    getContactChannels(contactId).then((rows) => {
      if (!alive) return
      const next = { whatsapp: rows.filter((r) => r.channel_type === 'phone').map((r) => r.value), emails: rows.filter((r) => r.channel_type === 'email').map((r) => r.value) }
      // Só sobrescreve o que veio do lead se o servidor tem canais desse tipo.
      setChannels((current) => ({ whatsapp: next.whatsapp.length ? next.whatsapp : current.whatsapp, emails: next.emails.length ? next.emails : current.emails }))
    }).catch(() => {})
    return () => { alive = false }
  }, [contactId])
  const edit = (type, add = true) => {
    const next = { whatsapp: channels.whatsapp.map(formatContactPhone), emails: channels.emails.map(formatContactEmail) }
    if (add) next[type].push('')
    setDraft(next)
    setError(''); setEditing(type)
  }
  const change = (type, index, value) => setDraft((current) => ({ ...current, [type]: current[type].map((entry, i) => i === index ? (type === 'whatsapp' ? formatContactPhone(value) : formatContactEmail(value)) : entry) }))
  const save = async (event) => {
    event.preventDefault()
    if (saving) return
    const next = { ...channels, [editing]: clean(draft[editing]) }
    if (editing === 'whatsapp' && next.whatsapp.some((phone) => !/^\+?[\d\s().-]+$/.test(phone) || !/^\d{10,15}$/.test(phone.replace(/\D/g, '')))) { setError('Informe números de WhatsApp válidos, com DDD e código do país quando necessário.'); return }
    if (editing === 'emails' && next.emails.some((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) { setError('Confira os endereços de e-mail.'); return }
    if (demo) {
      demoChannels.set(contactId, next)
      setChannels(next); setEditing(false); setError('')
      return
    }
    setSaving(true); setError('')
    try {
      const channelType = editing === 'whatsapp' ? 'phone' : 'email'
      const rows = await saveContactChannels(contactId, channelType, channelType === 'phone' ? clean(next[editing].map(toStoredPhone)) : next[editing])
      const saved = rows.filter((r) => r.channel_type === channelType).map((r) => r.value)
      setChannels((current) => ({ ...current, [editing]: saved }))
      onChannelsSaved?.({ [editing === 'whatsapp' ? 'telefone' : 'email']: saved[0] || null })
      setEditing(false)
    } catch (err) { setError(err.message || 'Não foi possível salvar os contatos.') }
    finally { setSaving(false) }
  }
  return <section className="dp-lead-contacts" aria-label="Contatos do lead">
    {[['whatsapp', 'WhatsApp'], ['emails', 'E-mail']].filter(([type]) => type === 'whatsapp' || showDetails).map(([type, label]) => <Fragment key={type}>
      <div className="dp-contact-group">
        <div className="dp-contact-heading"><span className="cv-row-label dp-contact-label">{label}</span></div>
        {editing === type ? <form onSubmit={save} onKeyDown={(event) => { if (event.key === 'Escape') { event.stopPropagation(); setEditing(false) } }}>
          {draft[type].map((value, index) => <div className="dp-contact-input" key={index}>
            <input aria-label={`${label} ${index + 1}`} type={type === 'emails' ? 'email' : 'tel'} inputMode={type === 'emails' ? 'email' : 'tel'} autoCapitalize="none" spellCheck={false} value={value} placeholder={type === 'emails' ? 'nome@empresa.com' : '+55 (11) 99999-9999'} onChange={(event) => change(type, index, event.target.value)} />
            <button type="button" aria-label={`Remover ${label} ${index + 1}`} onClick={() => setDraft((current) => ({ ...current, [type]: current[type].filter((_, i) => i !== index) }))}>×</button>
          </div>)}
          <button type="button" className="dp-contact-add" onClick={() => setDraft((current) => ({ ...current, [type]: [...current[type], ''] }))}>+ Adicionar {label}</button>
          {error && <p role="alert">{error}</p>}
          <div className="dp-contact-actions"><button type="button" disabled={saving} onClick={() => setEditing(false)}>Cancelar</button><button type="submit" disabled={saving}>{saving ? 'Salvando…' : 'Salvar'}</button></div>
        </form> : <>
          {channels[type].length ? channels[type].map((value, index) => <div className="dp-contact-value-row" key={`${value}-${index}`}>
            {type === 'whatsapp' && onOpenChat && <button type="button" className="dp-contact-chat" title="Abrir atendimento no Kanban" aria-label={`Abrir atendimento de ${value}`} onClick={() => onOpenChat(value)}><img src={whatsappIcon} alt="" width="18" height="18" /></button>}
            <button type="button" className="dp-contact-value" title={`Editar ${label}`} onClick={() => edit(type, false)}>{type === 'whatsapp' ? formatContactPhone(value) : value}</button>
            {index === 0 && <button type="button" className="dp-contact-plus" aria-label={`Adicionar ${label}`} onClick={() => edit(type)}>+</button>}
          </div>) : <div className="dp-contact-value-row"><button type="button" className="dp-contact-empty" onClick={() => edit(type)}>Adicionar {label}</button><button type="button" className="dp-contact-plus" aria-label={`Adicionar ${label}`} onClick={() => edit(type)}>+</button></div>}
        </>}
      </div>
      {type === 'whatsapp' && onToggleDetails && <button type="button" className="dp-contact-more" aria-expanded={showDetails} onClick={onToggleDetails}>{showDetails ? 'Ver menos' : 'Ver mais'}</button>}
    </Fragment>)}
  </section>
}
